import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Dialog, Sidebar, SidebarItem } from 'lynkco-up-core';
import { IconName } from 'lynkco-up-core/dist/types/components/Icon';
import { GenericFunction } from 'lynkco-up-core/dist/types/types';

import { routes } from '../../routing';
import { useLogout } from '../../hooks';
import msalInstance from '../../services/shared/auth';

type NavSection = {
  name: string;
  links: {
    label: string;
    icon?: IconName;
    path: string;
    badgeText?: string;
    current?: boolean;
    onClick?: GenericFunction;
    isAvailable?: boolean;
  }[];
};
const navSections: NavSection[] = [
  {
    name: 'Overview',
    links: [
      {
        label: routes.gdpr.name,
        icon: 'fingerprint',
        path: routes.gdpr.path,
        isAvailable: true,
      },
      {
        label: routes.applicants.name,
        icon: 'badge',
        path: routes.applicants.path,
        isAvailable: true,
      },
    ],
  },
];

function Layout() {
  const isSidebarVisibleAtStart = window.innerWidth >= 1280;
  const currentPath = useLocation().pathname.split('/').reverse()[0];
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(isSidebarVisibleAtStart);
  const sidebarWidth = '15rem';
  const sidebarOffset = isSidebarVisible ? 0 : `-${sidebarWidth}`;
  const outletWidth = isSidebarVisible ? `calc(100% - ${sidebarWidth})` : '100%';
  const outletOffset = isSidebarVisible ? sidebarWidth : 0;
  const sidebarToggleContainerClasses = isSidebarVisible
    ? 'fixed bottom-2 left-[11.875rem] z-20 transition-all'
    : 'fixed bottom-2 left-2 z-20 opacity-50 hover:opacity-100 transition-all';
  const sidebarToggleIcon = isSidebarVisible ? 'chevron_left' : 'chevron_right';
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const logout = useLogout(msalInstance);

  const navItems = navSections
    .filter(navItem => navItem.links.find(link => link.isAvailable))
    .map(navItem => ({
      ...navItem,
      links: navItem.links.map(link => ({
        ...link,
        current: link.path.includes(currentPath),
        onClick: () => handleNavigate(link.path),
      })),
    }));

  function handleNavigate(to: string) {
    navigate(to);
  }

  function handleToggleSidebar() {
    setIsSidebarVisible(!isSidebarVisible);
  }

  function handleOpenLogoutDialog() {
    setIsLogoutDialogOpen(true);
  }

  function handleCloseLogoutDialog() {
    setIsLogoutDialogOpen(false);
  }

  function handleLogout() {
    logout();
  }

  return (
    <div className="flex relative h-screen w-full">
      <div className={sidebarToggleContainerClasses}>
        <button
          className="bg-primary-100 rounded-lg text-primary-700 p-3 flex hover:bg-primary-300 hover:text-white transition-all"
          aria-label="toggle-sidebar"
          onClick={handleToggleSidebar}>
          <span className="m-icon icon-18">{sidebarToggleIcon}</span>
        </button>
      </div>
      <div className="h-screen fixed transition-all z-10" style={{ left: sidebarOffset }}>
        <Sidebar extraClasses="w-60">
          {navItems.map(navbarItem => (
            <div key={navbarItem.name}>
              <h3 className="mt-8 mb-2 text-xs font-semibold text-gray-600">{navbarItem.name}</h3>
              {navbarItem.links
                .filter(({ isAvailable }) => isAvailable)
                .map(({ label, icon, onClick, badgeText, current }) => (
                  <SidebarItem
                    key={label}
                    label={label}
                    icon={icon}
                    current={current}
                    onClick={onClick}
                    badgeText={badgeText}
                  />
                ))}
            </div>
          ))}
        </Sidebar>
        <div className="absolute bottom-2 left-2">
          <SidebarItem label="Sign out" icon="exit_to_app" onClick={handleOpenLogoutDialog} />
        </div>
      </div>
      <div className="relative transition-all overflow-x-hidden" style={{ width: outletWidth, left: outletOffset }}>
        <Outlet />
      </div>
      <Dialog
        isOpen={isLogoutDialogOpen}
        title="Confirm logout"
        body="Are you sure you want to log out of the application?"
        confirmText="Yes"
        cancelText="No"
        icon="exit_to_app"
        onConfirm={handleLogout}
        onCancel={handleCloseLogoutDialog}
        onClose={handleCloseLogoutDialog}
      />
    </div>
  );
}

export default Layout;
